export type FileSystemWriteChunk = Uint8Array | string;

export type FileSystemWritableFileStreamLike = {
  write: (chunk: FileSystemWriteChunk) => Promise<void>;
  close: () => Promise<void>;
  abort: (reason?: unknown) => Promise<void>;
};

type FileSystemWriterWindow = {
  showSaveFilePicker: (options: {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{ createWritable: () => Promise<FileSystemWritableFileStreamLike> }>;
};

/** Whether this browser supports writing files straight to disk (Chromium-based browsers only). */
export function supportsFileSystemWriter(): boolean {
  return typeof window !== "undefined" && typeof (window as unknown as FileSystemWriterWindow).showSaveFilePicker === "function";
}

/**
 * Opens a save-file picker and returns a writable stream for the chosen file. Only call where
 * `supportsFileSystemWriter()` is true. Rejects with a `DOMException` named "AbortError" if the
 * user cancels the dialog — callers should treat that as a no-op, not a failure.
 */
export async function pickWritableFile(
  suggestedName: string,
  types: { description: string; accept: Record<string, string[]> }[],
): Promise<FileSystemWritableFileStreamLike> {
  const { showSaveFilePicker } = window as unknown as FileSystemWriterWindow;
  const handle = await showSaveFilePicker({ suggestedName, types });
  return handle.createWritable();
}
